import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// COMPONENTS
import Avatar from 'components/common/Avatar';

// ICONS
import { Clock, X } from 'lucide-react';

// SERVICES
import { searchInAdmin } from 'services/searchService';

const categoryLabels = {
    users: 'Utilisateurs',
    tiles: 'Tuiles',
    tilesProposals: 'Propositions de tuiles',
    announcements: 'Annonces',
    usersWhitelistAnnouncement: 'Utilisateurs whitelistés',
    events: 'Évènements',
    resources: 'Ressources'
};

const SearchModal = ({ isOpen, onClose, searchValue, onSearch, searchBarRef }) => {
    const navigate = useNavigate();
    const [recentSearches, setRecentSearches] = useState([]);
    const [searchResults, setSearchResults] = useState({
        users: [],
        tiles: [],
        announcements: [],
        events: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef(null);
    const debounceTimeout = useRef();

    // Charger les recherches récentes depuis le localStorage
    useEffect(() => {
        const savedSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
        setRecentSearches(savedSearches);
    }, []);

    // Déclencher la recherche quand searchValue change (avec debounce)
    useEffect(() => {
        if (!isOpen) return;
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            handleSearch(searchValue);
        }, 1000); // 1000ms de délai
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [searchValue, isOpen]);

    // Sauvegarder une nouvelle recherche
    const saveSearch = (search) => {
        const searches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
        const newSearches = [search, ...searches.filter(s => s !== search)].slice(0, 5);
        localStorage.setItem('recentSearches', JSON.stringify(newSearches));
        setRecentSearches(newSearches);
    };

    // Gérer la recherche
    const handleSearch = async (value) => {
        if (!value.trim()) {
            setSearchResults({
                users: [],
                tiles: [],
                announcements: [],
                events: []
            });
            return;
        }

        setIsLoading(true);
        try {
            const results = await searchInAdmin(value);
            setSearchResults(results);
            saveSearch(value);
        } catch (error) {
            console.error('Erreur lors de la recherche:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Naviguer vers un résultat
    const handleResultClick = (path) => {
        onSearch(''); // Effacer la barre de recherche
        navigate(path);
        onClose();
    };

    if (!isOpen) return null;

    // Calculer la position et la largeur du modal en fonction de la SearchBar
    const searchBarRect = searchBarRef?.current?.getBoundingClientRect();
    const modalStyle = searchBarRect ? {
        position: 'absolute',
        top: `${searchBarRect.bottom + 8}px`,
        left: `${searchBarRect.left}px`,
        width: `${searchBarRect.width}px`,
    } : {};

    return (
        <div className="fixed inset-0 z-50" onClick={onClose}>
            <div 
                ref={modalRef}
                className="absolute bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark"
                style={modalStyle}
                onClick={e => e.stopPropagation()}
            >
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {searchValue ? 'Résultats de recherche' : 'Recherches récentes'}
                        </h3>
                        <button onClick={onClose}>
                            <X className="text-gray-400 hover:text-gray-600" size={20} />
                        </button>
                    </div>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                    {/* Recherches récentes */}
                    {!searchValue && recentSearches.length > 0 && (
                        <div className="p-4">
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {recentSearches.map((search, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            onSearch(search);
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-dark hover:bg-gray-200 dark:hover:bg-dark/80 rounded-full text-sm whitespace-nowrap"
                                    >
                                        <Clock className="text-gray-400" size={14} />
                                        <span className="text-noir dark:text-blanc">{search}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Résultats de recherche */}
                    {searchValue && (
                        <div className="p-4">
                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange mx-auto"></div>
                                    <p className="mt-2 text-gray-500 dark:text-gray-400">Recherche en cours...</p>
                                </div>
                            ) : (
                                <>
                                    {Object.entries(searchResults).map(([category, results]) => (
                                        results.length > 0 && (
                                            <div key={category} className="mb-4">
                                                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 capitalize">
                                                    {categoryLabels[category] || category}
                                                </h3>
                                                <div className="space-y-2">
                                                    {results.map((result) => (
                                                        <button
                                                            key={result.id}
                                                            onClick={() => handleResultClick(result.path)}
                                                            className="w-full flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-dark rounded-lg text-left"
                                                        >
                                                            {category === 'users' && (
                                                                <Avatar email={result.title} alt={result.title} className="size-8" rounded={true} />
                                                            )}
                                                            <span className="text-noir dark:text-blanc">{result.title}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    ))}
                                    {Object.values(searchResults).every(results => results.length === 0) && (
                                        <div className="text-center py-4">
                                            <p className="text-gray-500 dark:text-gray-400">Aucun résultat trouvé</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchModal; 